<template>
  <div
    ref="wrapperRef"
    class="h-full flex items-center lg:hidden"
    :class="{ [mobileWrapperOpened]: opened }"
    @keydown.esc="close()"
  >
    <base-lazy-hydrate :interaction="['click', 'focus']" when="interaction">
      <icon-menu
        v-model="opened"
        aria-controls="menu"
        :aria-expanded="opened"
        class="mx-2 cursor-pointer"
        :size="mobileNavIconSize"
        type="button"
        @click="toggle"
      >
        <span class="sr-only">{{ $t.menuIconLabel }}</span>
      </icon-menu>
    </base-lazy-hydrate>
    <transition
      enter-active-class="transform ease-out"
      enter-from-class="op-0"
      leave-active-class="transform ease-in"
      leave-to-class="op-0"
    >
      <div
        v-show="opened"
        id="menu"
        class="fixed-0 z-overlay overflow-y-auto transition"
        :class="mobileBackground"
        data-test-id="vf-mega-menu-mobile"
        :style="{ top: pxToRem(header.sticky.active ? header.sticky.margin : header.height.full) }"
      >
        <loyalty-rewards-accordion
          v-if="$feature.showLoyaltyRewardsInUtilityNav
            && user.isLoyaltyMember"
        />
        <lazy-cms-section
          v-if="megaMenu"
          :section="{ name: 'mobile-menu', items: [{ ...megaMenu, type: 'VfCanvasMegaMenuMobile' }] }"
        />
        <vf-utility-navigation
          v-if="utilityLinks"
          class="f-col items-start gap-4 b-t b-grey-70 p-4"
          :links="utilityLinks"
          @click="close(false)"
        />
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import type { MegaMenuContent } from '#types/components/cms/mega-menu'
import type { NavigationLink } from '#types/navigation'
import { HeaderContextKey } from '#commerce/components/vf/header/context'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'

defineProps<{
  megaMenu?: MegaMenuContent
  utilityLinks?: NavigationLink[]
}>()

const { lock, unlock } = useBodyScroll()
const header = useHeaderStore()
const { $gtm } = useNuxtApp()
const router = useRouter()
const { escape } = useMagicKeys()
const user = useUserStore()
const {
  brandClasses: { mobileBackground, mobileWrapperOpened },
  mobileNavIconSize
} = useAppConfig().components.cms.megaMenu

const { menuOpened } = inject(HeaderContextKey)!

const wrapperRef = ref<HTMLDivElement>()
const opened = ref(false)
let isEnableTrack = true

const close = (enableTrack = true) => {
  if (!enableTrack)
    isEnableTrack = false

  opened.value = false
}

const toggle = () => {
  opened.value = !opened.value
}

const { activate, deactivate } = useFocusTrap(wrapperRef, {
  checkCanFocusTrap: () => wait(250),
  clickOutsideDeactivates: true,
  initialFocus: false,
  onDeactivate: close
})

whenever(escape, () => opened.value = false)
watch(opened, async () => {
  await nextTick()
  menuOpened.value = opened.value
  if (opened.value) {
    activate()
    lock()
  }
  else {
    deactivate()
    unlock()
  }

  if (isEnableTrack)
    $gtm.push('topNav.onClickHamburgerMenu', opened.value)
  else
    isEnableTrack = true
})

router.beforeEach(() => {
  if (opened.value) close(false)
})
</script>
